import { useEffect, useState } from 'react';
import { logEvent } from '@amplitude/analytics-browser';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { TypographyProps } from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Grid, { GridProps } from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Button from '../../componentsv2/Button';
import TopBar from '../../componentsv2/TopBar';
import Typography from '../../componentsv2/Typography';
import { ArrowLeftIcon, ArrowRightIcon, IntroAppIcon } from '../../components/Icons';
import { newApplication } from '../../services/application';
import { useLazyTranslation } from '../../hooks/i18n';
import { useAuth } from '../../hooks';
import { useUserLocation } from '../../hooks/useUserLocation';
import { transformFactoringRequest, useAppSelector } from '../../store';
import { APIApplication } from '../../types/application';
import { Nullable } from '../../types';
import { APP_FLOW } from '../../constants/auth';
import { invoiceEvents } from '../../constants/events';
import { COLORS } from '../../themev2';
import classes from './Application.module.scss';

export const CustomGridItem = styled((props: GridProps) => <Grid {...props} />)(({ theme }) => ({
  paddingLeft: '0 !important',
}));

export const BulletedTypography = styled((props: TypographyProps) => <Typography {...props} />)(
  ({ theme }) => ({
    '&::before': {
      content: '"•"',
      marginRight: '8px',
    },
  })
);

const IntroAppScreen = ({
  setApplication,
  application,
}: {
  setApplication: (application: APIApplication) => void;
  application: Nullable<APIApplication>;
}) => {
  const { t, isLoading: isLoadingTranslations } = useLazyTranslation(['application']);
  const [isLoadingApplication, setIsLoadingApplication] = useState(false);
  const selectedCompany = useAppSelector((state) => state.companyUser.companyUser?.selectedCompany);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { location } = useUserLocation();

  const isLoading = isLoadingApplication || isLoadingTranslations;
  const isReapply = application?.status === 'application_rejected' && !application.isReapply;

  const handleStartApplication = () => {
    logEvent(invoiceEvents.START);
    setIsLoadingApplication(true);
    newApplication(selectedCompany!.id, isReapply)
      .then((application) => {
        setApplication(transformFactoringRequest(application, selectedCompany!));
        localStorage.setItem(APP_FLOW, application.flowType);
      })
      .finally(() => {
        setIsLoadingApplication(false);
      });
  };

  useEffect(() => {
    logEvent(invoiceEvents.INTRO);
  }, []);

  return (
    <>
      <TopBar>
        <Box sx={{ display: 'flex', alignItems: 'center', padding: '1em', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '24px', height: '24px' }}>
            <IconButton
              onClick={() => {
                navigate(-1);
              }}
            >
              <ArrowLeftIcon />
            </IconButton>
          </Box>
          <Box sx={{ marginX: 'auto' }}>
            <Typography>{t('application:intro.topBar')}</Typography>
          </Box>
        </Box>
      </TopBar>
      <div className={classes.icon}>
        <IntroAppIcon width={355} height={477} />
      </div>
      <Container
        maxWidth={false}
        sx={{ maxWidth: { xs: '100%', sm: 400, md: 724, lg: 1080 }, paddingBottom: '1rem' }}
      >
        <Grid container sx={{ marginTop: { xs: '8px', md: '40px' } }} rowSpacing={4}>
          <Grid item xs={12} md={8}>
            <Grid container>
              <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                <Typography
                  variant="h5"
                  sx={{
                    background: 'linear-gradient(255.79deg, #4572B8 0%, #2FC0B8 95.35%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textAlign: { xs: 'left', md: 'initial' },
                  }}
                >
                  {t('application:intro.title', { name: user?.firstName })}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#0A1334', textAlign: { xs: 'left', md: 'initial' } }}>
                  {t('application:intro.subtitle')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#66758F',
                    whiteSpace: 'pre-line',
                    textAlign: { xs: 'left', md: 'initial' },
                  }}
                >
                  {t('application:intro.description')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={7}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={7}>
            <Grid container rowSpacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ color: COLORS.secondary.indigo, textAlign: { xs: 'left', md: 'initial' } }}
                >
                  {t('application:intro.requirements.title')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <BulletedTypography
                  variant="body1"
                  sx={{ color: '#4F5A84', textAlign: { xs: 'left', md: 'initial' } }}
                >
                  {t('application:intro.requirements.generalInfo')}
                </BulletedTypography>
                {/* not the most correct approach ever but it's way easier than using the flow type that's not set yet */}
                {location === 'mx' && (
                  <BulletedTypography
                    variant="body1"
                    sx={{ color: '#4F5A84', textAlign: { xs: 'left', md: 'initial' } }}
                  >
                    {t('application:intro.requirements.SAT')}
                  </BulletedTypography>
                )}
                <BulletedTypography
                  variant="body1"
                  sx={{ color: '#4F5A84', textAlign: { xs: 'left', md: 'initial' } }}
                >
                  {t('application:intro.requirements.customersData')}
                </BulletedTypography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={7}>
            <Grid container>
              <Grid item xs={12} md={8}>
                <Button
                  // Once the application has the flag isReapply as true, we don't want the clients to reapply again.
                  disabled={application?.isReapply}
                  fullWidth
                  onClick={handleStartApplication}
                  isLoading={isLoading}
                  endIcon={isLoading ? null : <ArrowRightIcon />}
                >
                  {t('application:startApplicationButton')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default IntroAppScreen;
